<template>
  <div class="layout">
    <aside class="saved-list">
      <h3>Saved</h3>
      <ul>
        <li v-for="calc in calculators" :key="calc.id">
          <span @click="loadCalculator(calc)">{{ calc.title || '(Untitled)' }}</span>
        </li>
      </ul>
      <div v-if="totalPages > 1" class="pagination">
        <button :disabled="page === 1" @click="fetchCalculators(page - 1)">Prev</button>
        <span>Page {{ page }} / {{ totalPages }}</span>
        <button :disabled="page === totalPages" @click="fetchCalculators(page + 1)">Next</button>
      </div>
      <div v-if="loadingList" class="loading-list">Loading...</div>
    </aside>
    <div class="main-card">
      <!-- <h1>Weaving Costing Calculator</h1> -->
      <input
        class="title-input"
        v-model="title"
        :class="{ invalid: errors.title }"
        maxlength="50"
        spellcheck="false"
        placeholder="Enter Title"
        aria-label="Calculator Title" 
      />
      <div class="section">
        <h2>General Inputs</h2>
        <div class="input-grid weft-grid">
          <label>
            <span>Tar</span>
            <input type="number" v-model="tar" :class="{ invalid: errors.tar }">
          </label>
          <label>
            <span>Pano</span>
            <input type="number" v-model="pano" :class="{ invalid: errors.pano }">
          </label>
          <label>
            <span>RPM</span>
            <input type="number" v-model="rpm">
          </label>
          <label>
            <span>Khat khar {{ khataKharchSuggestion }}</span>
            <input type="number" v-model="khatakharch" :class="{ invalid: errors.khatakharch }">
          </label>
        </div>
      </div>

      <div class="section">
        <h2>Wrap Inputs</h2>
        <div class="input-grid weft-grid">
          <label>
            <span>Denier</span>
            <input type="number" v-model="wrapDenier" :class="{ invalid: errors.wrapDenier }">
          </label>
          <label>
            <span>Wastage (%)</span>
            <input type="number" v-model="wrapWastage" :class="{ invalid: errors.wrapWastage }">
          </label>
          <label>
            <span>Yarn Price</span>
            <input type="number" v-model="wrapYarnPrice" :class="{ invalid: errors.wrapYarnPrice }">
          </label>
          <label>
            <span>Cartage (%)</span>
            <input type="number" v-model="wrapCartage" :class="{ invalid: errors.wrapCartage }">
          </label>
          <label>
            <span>GST (%)</span>
            <input type="number" v-model="wrapGst" :class="{ invalid: errors.wrapGst }">
          </label>
        </div>
      </div>

      <div class="section">
        <h2>Weft Patterns</h2>
        <div v-if="wefts.length > 1" class="input-grid">
          <label>
            <span>Total Pick</span>
            <input type="number" v-model="totalPick" :class="{ invalid: errors.totalPick }">
          </label>
          <label>
            <span>Total Pattern</span>
            <input type="number" v-model="totalPattern" :class="{ invalid: errors.totalPattern }">
          </label>
        </div>
        <div v-for="(weft, index) in wefts" :key="index" class="pattern-row">
          <div class="pattern-header">
            <span>Weft {{ index + 1 }}</span>
            <button v-if="wefts.length > 1" class="remove-btn" @click="removeWeft(index)" title="Remove">&times;</button>
          </div>
          <div class="input-grid weft-grid">
            <label>
              <span>Pick</span>
              <input type="number" v-model="weft.pick" :class="{ invalid: errors[`weft_pick_${index}`] }">
            </label>
            <label>
              <span>Denier</span>
              <input type="number" v-model="weft.denier" :class="{ invalid: errors[`weft_denier_${index}`] }">
            </label>
            <label>
              <span>Yarn Price</span>
              <input type="number" v-model="weft.yarnPrice" :class="{ invalid: errors[`weft_yarnPrice_${index}`] }">
            </label>
            <label>
              <span>Shortage (%)</span>
              <input type="number" v-model="weft.shortage" :class="{ invalid: errors[`weft_shortage_${index}`] }">
            </label>
            <label>
              <span>GST (%)</span>
              <input type="number" v-model="weft.gst" :class="{ invalid: errors[`weft_gst_${index}`] }">
            </label>
          </div>
        </div>
        <button type="button" class="add-btn" @click="addWeftRow">+ Add Weft</button>
      </div>

      <div v-if="errorMessage" class="validation-error">{{ errorMessage }}</div>

      <div style="text-align:center;">
        <button type="button" class="calc-btn" @click="calculate">📊 Calculate</button>
        <button type="button" class="cloud-btn" @click="saveToSupabase" :disabled="saving">
          <span v-if="saving">Saving...</span>
          <span v-else>💾 Save to Cloud</span>
        </button>
      </div>
      <div v-if="saveMessage" class="saved-msg">{{ saveMessage }}</div>
      <div class="section">
        <h2>Results </h2>
        <div class="result" v-html="result"></div>
      </div>
      
    </div>

    <button class="fab-new-btn" @click="newCalculation" title="New Calculation">
      ＋
    </button>
  </div>
</template>

<script>
import { supabase } from '../supabase';

function generateId() {
  return 'calc_' + Math.random().toString(36).substr(2, 9);
}

function getIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('id');
}

export default {
  props: {
    user: Object
  },
  data() {
    return {
      id: null,
      title: '',
      tar: null,
      pano: null,
      totalPick: null,
      totalPattern: null,
      rpm: 700,
      khatakharch: 10,
      wrapDenier: null,
      wrapWastage: null,
      wrapYarnPrice: null,
      wrapCartage: 1,
      wrapGst: 5,
      wefts: [
        { pick: null, denier: null, shortage: 8, yarnPrice: null, gst: 5 }
      ],
      result: '',
      errors: {},
      errorMessage: '',
      calculators: [],
      page: 1,
      pageSize: 100,
      totalPages: 1,
      loadingList: false,
      isMobile: false,
      drawerOpen: false,
      saving: false,
      saveMessage: ''
    };
  },
  computed: {
    khataKharchSuggestion() {
      const settings = JSON.parse(localStorage.getItem('khataKharchSettings') || '{}');
      const { ...rest } = settings;
      const sumForAllMachines = Object.values(rest).reduce((a, b) => a + Number(b || 0), 0);
      const machines = Number(settings.machines) || 1;
      let totalPick = parseFloat(this.totalPick) || 0;
      if (this.wefts.length < 2) {
        totalPick = parseFloat(this.wefts[0].pick);
      }
      const rpm = parseFloat(this.rpm) || 0;
      let prod = 1;
      if (rpm > 0) {
        prod = (((1440 * rpm) / totalPick) / 39.37) * 0.9;
      }
      if (!machines || !prod) return 0;
      return (sumForAllMachines / (prod * machines * 28)).toFixed(2);
    }
  },
  watch: {
    user: {
      handler(newVal, oldVal) {
        if (
          newVal &&
          newVal.id &&
          (!oldVal || newVal.id !== oldVal.id)
        ) {
          this.fetchCalculators(1);
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    addWeftRow() {
      this.wefts.push({ pick: null, denier: null, shortage: 8, yarnPrice: null, gst: 5 });
    },
    removeWeft(index) {
      this.wefts.splice(index, 1);
    },
    validate() {
      this.errors = {};
      this.errorMessage = '';
      if (!this.title || !this.title.trim()) {
        this.errors.title = true;
      }
      if (!this.tar && this.tar !== 0) this.errors.tar = true;
      if (!this.pano && this.pano !== 0) this.errors.pano = true;
      if (!this.khatakharch && this.khatakharch !== 0) this.errors.khatakharch = true;
      if (!this.wrapDenier && this.wrapDenier !== 0) this.errors.wrapDenier = true;
      if (!this.wrapWastage && this.wrapWastage !== 0) this.errors.wrapWastage = true;
      if (!this.wrapYarnPrice && this.wrapYarnPrice !== 0) this.errors.wrapYarnPrice = true;
      if (!this.wrapCartage && this.wrapCartage !== 0) this.errors.wrapCartage = true;
      if (!this.wrapGst && this.wrapGst !== 0) this.errors.wrapGst = true;
      if (this.wefts.length > 1) {
        if (!this.totalPick && this.totalPick !== 0) this.errors.totalPick = true;
        if (!this.totalPattern && this.totalPattern !== 0) this.errors.totalPattern = true;
      }
      this.wefts.forEach((weft, i) => {
        if (!weft.pick && weft.pick !== 0) this.errors[`weft_pick_${i}`] = true;
        if (!weft.denier && weft.denier !== 0) this.errors[`weft_denier_${i}`] = true;
        if (!weft.shortage && weft.shortage !== 0) this.errors[`weft_shortage_${i}`] = true;
        if (!weft.yarnPrice && weft.yarnPrice !== 0) this.errors[`weft_yarnPrice_${i}`] = true;
        if (!weft.gst && weft.gst !== 0) this.errors[`weft_gst_${i}`] = true;
      });
      if (Object.keys(this.errors).length) {
        this.errorMessage = "Please fill all required fields.";
        return false;
      }
      return true;
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 600;
      if (!this.isMobile) this.drawerOpen = false;
    },
    async saveToSupabase() {
      if (!this.validate()) {
        this.saveMessage = "Please fill all required fields before saving.";
        return;
      }

      this.saving = true;
      this.saveMessage = '';

      const calculatorData = {
        tar: this.tar,
        pano: this.pano,
        totalPick: this.totalPick,
        totalPattern: this.totalPattern,
        rpm: this.rpm,
        khatakharch: this.khatakharch,
        wrapDenier: this.wrapDenier,
        wrapWastage: this.wrapWastage,
        wrapYarnPrice: this.wrapYarnPrice,
        wrapCartage: this.wrapCartage,
        wrapGst: this.wrapGst,
        wefts: this.wefts,
      };

      let error;

      if (this.id) {
        // Update existing row (by id)
        ({ error } = await supabase
          .from('calculators')
          .update({
            title: this.title,
            data: calculatorData,
          })
          .eq('id', this.id));
      } else {
        // Insert new row
        const { data, error: insertError } = await supabase
          .from('calculators')
          .insert([
            {
              title: this.title,
              data: calculatorData,
            }
          ])
          .select()
          .single(); // so we get the new row back

        error = insertError;
        if (!error && data) {
          this.id = data.id; // save id for future updates
        }
      }

      this.saving = false;
      if (error) {
        this.saveMessage = "Error saving to cloud: " + error.message;
      } else {
        this.saveMessage = this.id ? "Updated in cloud!" : "Saved to cloud!";
      }
    }
,
    calculate() {
      if (!this.validate()) {
        this.result = '';
        return;
      }
      // Remove localStorage logic

      const tar = parseFloat(this.tar) || 0;
      const pano = parseFloat(this.pano) || 0;
      let totalPick = parseFloat(this.totalPick) || 0;
      let totalPattern = parseFloat(this.totalPattern) || 1;

      if (this.wefts.length < 2) {
        totalPick = parseFloat(this.wefts[0].pick);
        totalPattern = totalPick;
      }

      const rpm = parseFloat(this.rpm) || 0;
      const khatakharch = parseFloat(this.khatakharch) || 0;

      const wrapDenier = parseFloat(this.wrapDenier) || 0;
      const wrapWastage = parseFloat(this.wrapWastage) || 0;
      const wrapYarnPrice = parseFloat(this.wrapYarnPrice) || 0;
      const wrapCartage = parseFloat(this.wrapCartage) || 0;
      const wrapGst = parseFloat(this.wrapGst) || 5;

      const patternRatio = totalPick / totalPattern;
      const wrapRequire = (tar * wrapDenier * (100 + wrapWastage)) / 9000000;
      const wrapPricePerKg = wrapYarnPrice + (wrapYarnPrice * (wrapCartage + wrapGst) / 100);
      const wrapPricePerMeter = (wrapPricePerKg * wrapRequire) / 100;

      let totalWeftPricePerMeter = 0;
      let fabricWeftWeight = 0;
      let production = 0;
      let eachWeftPrice = '';

      if (rpm > 0) {
        production = (((1440 * rpm) / totalPick) / 39.37) * 0.9;
      }
      
      this.wefts.forEach((weft, index) => {
        const pick = parseFloat(weft.pick) || 0;
        const denier = parseFloat(weft.denier) || 0;
        const shortage = parseFloat(weft.shortage) || 0;
        const yarnPrice = parseFloat(weft.yarnPrice) || 0;
        const gst = parseFloat(weft.gst) || 5;

        const weftPickRatio = pick * patternRatio;
        const weftRequire = (pano * weftPickRatio * denier * (100 + shortage)) / 9000000;
        const weftPricePerKg = yarnPrice + (yarnPrice * gst / 100);
        const weftPricePerMeter = (weftPricePerKg * weftRequire) / 100;

        totalWeftPricePerMeter += weftPricePerMeter;
        fabricWeftWeight += (pano * weftPickRatio * denier) / 90000;

        eachWeftPrice += `<td style="text-align: left;"><b>Weft ${index + 1} Price per meter</b><td>₹${weftPricePerMeter.toFixed(2)}</td>`;
        eachWeftPrice += `<td style="text-align: left;"><b>Weft ${index + 1} Weight per 100m</b><td>${weftRequire.toFixed(2)} kg</td>`;
      });

      const fabricWeight = (tar * wrapDenier) / 90000 + fabricWeftWeight;
      const totalYarnPrice = wrapPricePerMeter + totalWeftPricePerMeter;
      const costingPerMeter = totalYarnPrice + khatakharch;

      this.result = `
        <table class="result-table" style="width:100%;">
          <tr>
            <td style="text-align: left;"><b>Total Yarn Per meter</b></td><td>₹${totalYarnPrice.toFixed(2)}</td>            
            <td style="text-align: left;"><b>Production</b></td><td>${production.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="text-align: left;"><b>Wrap Price Per meter</b></td><td>₹${wrapPricePerMeter.toFixed(2)}</td>
            <td style="text-align: left;"><b>Wrap Weight Per 100m</b></td><td>${wrapRequire.toFixed(2)} kg</td>
          </tr>
          <tr>
            ${eachWeftPrice}
          </tr>
          <tr>
            <td style="text-align: left;"><b>Costing:</b></td><td>₹${costingPerMeter.toFixed(2)}</td>              
            <td style="text-align: left;"><b>Weight:</b></td><td>${fabricWeight.toFixed(3)} kg</td>
          </tr> 
        </table>
      `;
    },
    newCalculation() {
      this.id = null;
      this.title = '';
      this.tar = null;
      this.pano = null;
      this.totalPick = null;
      this.totalPattern = null;
      this.rpm = 700;
      this.khatakharch = 10;
      this.wrapDenier = null;
      this.wrapWastage = 8;
      this.wrapYarnPrice = null;
      this.wrapCartage = 1;
      this.wrapGst = 5;
      this.wefts = [
        { pick: null, denier: null, shortage: 8, yarnPrice: null, gst: 5 }
      ];
      this.result = '';
      this.errors = {};
      this.errorMessage = '';
      const url = new URL(window.location);
      url.searchParams.delete('id');
      window.history.replaceState({}, '', url);
    },
    async fetchCalculators(page = 1) {
      if (!this.user || !this.user.id) return;
      this.loadingList = true;
      const from = (page - 1) * this.pageSize;
      const to = from + this.pageSize - 1;
      const { data, error } = await supabase.from("user_calculators").select("*")
      const count = 100;

      if (!error) {
        this.calculators = data || [];
        this.totalPages = count ? Math.ceil(count / this.pageSize) : 1;
        this.page = page;
      }
      this.loadingList = false;
    },
    loadCalculator(calc) {
      this.id = calc.id;
      this.title = calc.title;
      Object.assign(this, calc.data);
      this.result = '';
      this.errorMessage = '';
    },
  }
};
</script>

<style scoped>
.layout {
  display: flex;
  flex-direction: row;
  min-height: calc(100vh - 80px);
}
.saved-list {
  width: 200px;
  background: #f4f6f9;
  border-right: 1px solid #e0e0e0;
  padding: 18px 8px 8px 8px;
  min-height: 100vh;
  box-sizing: border-box;
}
.saved-list h3 {
  font-size: 1rem;
  margin-bottom: 10px;
  color: #2563eb;
  text-align: left;
}
.saved-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.saved-list li {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 0;
  border-bottom: 1px solid #eaeaea;
  cursor: pointer;
}
.saved-list li span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
}
.pagination button {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 4px 12px;
  font-size: 1rem;
  cursor: pointer;
}
.pagination button:disabled {
  background: #b2dfdb;
  cursor: not-allowed;
}
.loading-list {
  text-align: center;
  color: #2563eb;
  margin-top: 8px;
}
.drawer-toggle {
  position: fixed;
  top: 12px;
  left: 12px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 1.5em;
  padding: 4px 10px;
  z-index: 1001;
  cursor: pointer;
}
.drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 80vw;
  max-width: 260px;
  height: 100vh;
  background: #f4f6f9;
  box-shadow: 2px 0 8px rgba(0,0,0,0.08);
  z-index: 1002;
  padding: 0 0 0 0;
  display: flex;
  flex-direction: column;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 14px 8px 14px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 1.1em;
  color: #2563eb;
}
.drawer ul {
  list-style: none;
  padding: 10px 14px;
  margin: 0;
}
.drawer li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid #eaeaea;
  cursor: pointer;
}
.drawer li.active span {
  font-weight: bold;
  color: #1746a2;
}
.drawer li span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-btn {
  background: none;
  border: none;
  color: #e74c3c;
  font-size: 1.1em;
  cursor: pointer;
  margin-left: 6px;
}
.delete-btn:hover {
  color: #c0392b;
}
.main-card {
  flex: 1;
  max-width: 540px;
  margin: 32px auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 18px 18px 28px 18px;
}
h1 {
  text-align: center;
  color: #2563eb;
  font-size: 1.2rem;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}
h2 {
  font-size: 1rem;
  margin: 12px 0 8px 0;
  color: #34495e;
  border-left: 3px solid #3498db;
  padding-left: 8px;
  text-align: left;
}
.section {
  margin: 10px 0 0 0;
  padding: 10px 0 0 0;
}
.input-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px 12px;
  align-items: center;
}
label {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
input {
  width: 100%;
  padding: 4px 7px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  background: #f8fafc;
  transition: border 0.2s;
}
input:focus {
  border: 1.5px solid #2563eb;
  outline: none;
}
input.invalid {
  border: 1.5px solid #e74c3c;
  background: #fff6f6;
}
.title-input.invalid {
  border-bottom: 2px solid #e74c3c;
  background: #fff6f6;
}
.validation-error {
  color: #e74c3c;
  background: #fff6f6;
  border: 1px solid #e74c3c;
  border-radius: 5px;
  padding: 7px 10px;
  margin: 12px 0 0 0;
  font-size: 13px;
  text-align: center;
}
.pattern-row {
  background: #f8fafd;
  border: 1px solid #e3e7ee;
  padding: 8px 10px 4px 10px;
  margin: 8px 0 0 0;
  border-radius: 7px;
}
.pattern-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}
.weft-grid {
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 6px 8px;
}
.add-btn, .calc-btn {
  margin-top: 10px;
  padding: 6px 16px;
  background: #2563eb;
  border: none;
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 1px 2px rgba(37,99,235,0.07);
}
.add-btn:hover, .calc-btn:hover {
  background: #1746a2;
}
.remove-btn {
  background: #e74c3c;
  border: none;
  color: #fff;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  font-size: 15px;
  cursor: pointer;
  margin-left: 8px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.remove-btn:hover {
  background: #c0392b;
}
.result {
  margin-top: 18px;
  padding: 14px 10px 10px 10px;
  border-radius: 8px;
  background: #ecf9f2;
  border: 1px solid #2ecc71;
  font-size: 13px;
}
.result strong {
  font-size: 14px;
  color: #27ae60;
}
.result-table {
  margin-top: 8px;
  border-collapse: collapse;
  width: 100%;
}
.result-table td {
  padding: 5px 7px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
}
.result-table td:first-child {
  font-weight: 500;
  color: #2c3e50;
}
.result-table tr:nth-child(4n+1),
.result-table tr:nth-child(4n+2) {
    background-color: #fdfdfd;
}
.result-table tr:nth-child(4n+3),
.result-table tr:nth-child(4n+4) {
    background-color: #f6f6f6;
}
.result-table tr:hover {
    background-color: #e8f7f1;
}
.summary {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
}
.title-input {
  display: block;
  margin: 0 auto 10px auto;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2563eb;
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  max-width: 420px;
  letter-spacing: 0.5px;
  padding: 6px 0 6px 0;
  border-bottom: 2px solid #2563eb;
  transition: border-color 0.2s;
}
.title-input:focus {
  border-bottom: 2px solid #1746a2;
  background: #f4f6f9;
}
.fab-new-btn {
  position: fixed;
  bottom: 16px;
  right: 16px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  transition: background 0.2s;
  z-index: 1001;
}
.fab-new-btn:hover {
  background: #1746a2;
}
.copy-btn {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 1.1em;
  cursor: pointer;
  margin-left: 6px;
  border-radius: 3px;
  padding: 2px 6px;
  transition: background 0.2s;
}
.copy-btn:hover {
  background: #eaf3ff;
  color: #1746a2;
}
.cloud-btn {
  background: #27ae60;
  color: #fff;
  font-size: 1rem;
  padding: 7px 22px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-left: 10px;
  font-weight: 500;
  transition: background 0.2s;
}
.cloud-btn:disabled {
  background: #b2dfdb;
  cursor: not-allowed;
}
.saved-msg {
  color: #27ae60;
  text-align: center;
  margin-top: 10px;
  font-size: 13px;
}
@media (max-width: 600px) {
  .input-grid, .weft-grid {
    grid-template-columns: 1fr;
  }
  .input-grid label,
  .weft-grid label {
    flex-direction: row;
    align-items: center;
    gap: 10px;
    justify-content: flex-start;
  }
  .input-grid label > input,
  .weft-grid label > input {
    flex: 1 1 0;
    min-width: 0;
    margin-left: 0;
    max-width: 180px;
  }
  .input-grid label,
  .weft-grid label {
    min-width: 0;
  }
  .input-grid label::before,
  .weft-grid label::before {
    content: '';
  }
  .input-grid label span,
  .weft-grid label span {
    display: inline-block;
    min-width: 80px;
    text-align: left;
  }
  .input-grid label,
  .weft-grid label {
    width: 100%;
  }
  .input-grid label {
    /* Make label text fixed width for alignment */
  }
  .summary {
    flex-direction: column;
    gap: 4px;
  }
  h2 {
    text-align: left;
  }
  
  .layout {
    flex-direction: column;
  }
  .saved-list {
    display: none;
  }
  .main-card {
    margin: 32px auto;
  } 
  .fab-new-btn {
    right: 16px;
    bottom: 16px;
    width: 48px;
    height: 48px;
    font-size: 1.7rem;
  } 
}
@media (min-width: 601px) {
  .drawer-toggle, .drawer {
    display: none !important;
  }
  .main-card {
    flex: 1;
  }
}
</style>